import React, {
  useState,
  useLayoutEffect,
  useRef,
  useEffect,
  useCallback,
} from "react";
import debounce from "lodash/debounce";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import LogoutButton from "./LogoutButton";
import ImageFile from "../images/afrogebeya.png";
import "../styles/header.css";

const PRODUCT_API = process.env.REACT_APP_PRODUCT_SERVICE_URL;
const IMAGE_BASE = "https://afrogebeya.com/uploads/products";
const PLACEHOLDER = "https://via.placeholder.com/40x40?text=+";

const resolveThumb = (url) =>
  url ? `${IMAGE_BASE}/${url.replace(/\\/g, "/")}` : PLACEHOLDER;

const formatPrice = (value, currency = "ETB") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
  }).format(value || 0);

const createSlug = (name = "", id) =>
  `${name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")}-${id}`;

/* ─────────────────────────────────────────
   Main component
───────────────────────────────────────── */
function Header({ hideExtras = false, categories = [] }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const searchWrapperRef = useRef(null);
  const userMenuRef = useRef(null);
  const hideTimerRef = useRef(null);

  const { auth, isAuthenticated } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const displayName = auth?.firstName ?? "User";
  const userInitials = (auth?.firstName?.[0] ?? "U").toUpperCase();
  const userRole = auth?.roles?.includes("SELLER")
    ? "Seller Account"
    : "Buyer Account";

  const hideExtrasOnPath =
    location.pathname.startsWith("/seller") ||
    location.pathname.startsWith("/admin");

  const shouldHide = hideExtras || hideExtrasOnPath;

  /* ── Suggestions fetch ── */
  const fetchSuggestions = useCallback(
    debounce(async (value) => {
      if (!value.trim()) {
        setSuggestions([]);
        return;
      }
      try {
        const res = await fetch(
          `${PRODUCT_API}/products/search/suggestions?q=${encodeURIComponent(value)}`,
        );
        if (res.ok) setSuggestions(await res.json());
      } catch {
        /* fail silently */
      }
    }, 300),
    [],
  );

  useEffect(() => {
    fetchSuggestions(searchTerm);
  }, [searchTerm, fetchSuggestions]);

  /* ── Close suggestions on outside click ── */
  useEffect(() => {
    const handler = (e) => {
      if (
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(e.target)
      ) {
        setSuggestions([]);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ── Lock body scroll when drawer is open ── */
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  /* ── Offset main content below fixed header ── */
  // useLayoutEffect(() => {
  //   const header = document.querySelector(".hd-root");
  //   const main = document.querySelector("#main-content");
  //   if (header && main) {
  //     main.style.paddingTop = `${header.offsetHeight}px`;
  //   }
  // }, [location.pathname]);

  useLayoutEffect(() => {
    const updateOffset = () => {
      const header = document.querySelector(".hd-root");
      const main = document.querySelector("#main-content");

      if (header && main) {
        main.style.paddingTop = `${header.offsetHeight}px`;
      }
    };

    updateOffset();

    window.addEventListener("resize", updateOffset);

    return () => {
      window.removeEventListener("resize", updateOffset);
    };
  }, [location.pathname, categories.length]);

  /* ── Search submit ── */
  const handleSearch = (e) => {
    e.preventDefault();
    if (activeIndex >= 0 && suggestions[activeIndex]) {
      const s = suggestions[activeIndex];
      navigate(`/products/${createSlug(s.productName, s.id)}`);
    } else if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
    }
    setSuggestions([]);
    setDrawerOpen(false);
  };

  /* ── Keyboard navigation in suggestions ── */
  const handleKeyDown = (e) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    }
    if (e.key === "Enter" && activeIndex >= 0) {
      const s = suggestions[activeIndex];
      navigate(`/products/${createSlug(s.productName, s.id)}`);
      setSuggestions([]);
    }
    if (e.key === "Escape") setSuggestions([]);
  };

  /* ── User menu auto-close ── */
  const startAutoClose = () => {
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setShowUserMenu(false), 3000);
  };

  const handleSuggestionClick = (item) => {
    navigate(`/products/${createSlug(item.productName, item.id)}`);
    setSuggestions([]);
    setSearchTerm("");
  };

  const closeDrawer = () => setDrawerOpen(false);

  /* ─────────────────────────────────────────
     Render
  ───────────────────────────────────────── */
  return (
    <>
      <header className="hd-root" role="banner">
        {/* ── Main row ── */}
        <div className="hd-main">
          {/* Logo */}
          <Link to="/" className="hd-logo" aria-label="Afrogebeya home">
            <img src={ImageFile} alt="" className="hd-logo__img" />
            <span className="hd-logo__name">
              Afro<span>gebeya</span>
            </span>
          </Link>

          {/* Search — desktop */}
          {!shouldHide && (
            <div ref={searchWrapperRef} className="hd-search">
              <form
                onSubmit={handleSearch}
                className="hd-search__form"
                role="search"
              >
                <input
                  type="search"
                  className="hd-search__input"
                  placeholder="Search products, brands, categories…"
                  value={searchTerm}
                  autoComplete="off"
                  aria-label="Search"
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setActiveIndex(-1);
                  }}
                  onKeyDown={handleKeyDown}
                />
                <button
                  type="submit"
                  className="hd-search__btn"
                  aria-label="Submit search"
                >
                  <i className="bi bi-search" />
                </button>
              </form>

              {/* Suggestions */}
              {suggestions.length > 0 && (
                <div
                  className="hd-suggestions"
                  role="listbox"
                  aria-label="Search suggestions"
                >
                  {suggestions.slice(0, 6).map((item, i) => (
                    <div
                      key={item.id}
                      role="option"
                      aria-selected={i === activeIndex}
                      className={`hd-suggestion${i === activeIndex ? " hd-suggestion--active" : ""}`}
                      onClick={() => handleSuggestionClick(item)}
                    >
                      <img
                        src={resolveThumb(item.mainImageUrl)}
                        alt=""
                        className="hd-suggestion__img"
                        onError={(e) => {
                          e.target.src = PLACEHOLDER;
                        }}
                      />
                      <span className="hd-suggestion__name">
                        {item.productName}
                      </span>
                      <span className="hd-suggestion__price">
                        {formatPrice(item.price, item.currency)}
                      </span>
                    </div>
                  ))}
                  <div
                    className="hd-suggestions__footer"
                    role="option"
                    onClick={() => {
                      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
                      setSuggestions([]);
                    }}
                  >
                    See all results for "{searchTerm}" →
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Right actions */}
          {!hideExtras && (
            <div className="hd-actions">
              {/* Cart */}
              <Link
                to="/cart"
                className="hd-cart"
                aria-label={`Cart, ${cartCount} items`}
              >
                <i className="bi bi-cart" />
                {cartCount > 0 && (
                  <span className="hd-cart__badge" aria-hidden="true">
                    {cartCount > 99 ? "99+" : cartCount}
                  </span>
                )}
              </Link>

              {isAuthenticated ? (
                /* User menu */
                <div
                  ref={userMenuRef}
                  className="hd-user"
                  onMouseEnter={() => clearTimeout(hideTimerRef.current)}
                  onMouseLeave={startAutoClose}
                >
                  <button
                    className="hd-user__btn"
                    aria-expanded={showUserMenu}
                    aria-haspopup="true"
                    onClick={() => {
                      setShowUserMenu((v) => !v);
                      startAutoClose();
                    }}
                  >
                    <div className="hd-user__avatar" aria-hidden="true">
                      {userInitials}
                    </div>
                    <span className="hd-user__name">{displayName}</span>
                    <i
                      className="bi bi-chevron-down hd-user__chevron"
                      aria-hidden="true"
                    />
                  </button>

                  <div
                    className={`hd-user__dropdown${showUserMenu ? " hd-user__dropdown--open" : ""}`}
                    role="menu"
                  >
                    <div className="hd-dropdown__header">
                      <div className="hd-dropdown__header-name">
                        {displayName}
                      </div>
                      <div className="hd-dropdown__header-role">{userRole}</div>
                    </div>

                    <Link
                      to="/buyer/profile"
                      className="hd-dropdown__item"
                      role="menuitem"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <i className="bi bi-person" /> My Profile
                    </Link>
                    <Link
                      to="/buyer/orders"
                      className="hd-dropdown__item"
                      role="menuitem"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <i className="bi bi-box-seam" /> My Orders
                    </Link>
                    <Link
                      to="/buyer/wishlist"
                      className="hd-dropdown__item"
                      role="menuitem"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <i className="bi bi-heart" /> Wishlist
                    </Link>
                    <Link
                      to="/buyer/settings"
                      className="hd-dropdown__item"
                      role="menuitem"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <i className="bi bi-gear" /> Settings
                    </Link>
                    <hr className="hd-dropdown__divider" />
                    <LogoutButton
                      closeMenu={() => setShowUserMenu(false)}
                      className="hd-dropdown__item hd-dropdown__item--danger"
                    />
                  </div>
                </div>
              ) : (
                /* Auth links */
                <>
                  <Link to="/login" className="hd-auth hd-auth--login">
                    Login
                  </Link>
                  <a
                    href="https://seller.afrogebeya.com"
                    className="hd-sell"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Sell<span className="hd-sell__long"> on Afrogebeya</span>
                  </a>
                </>
              )}
            </div>
          )}
        </div>
      </header>
    </>
  );
}

export default Header;
